import React, { useMemo, useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useAppDispatch } from "../../../redux/store";
import { emailRegex } from "../../../constants/common";
import { auth, getUserName } from "../../../firebase";
import { setUserName } from "../../../redux/userSlice";
import SignInView from "../ui/SignInView";
import { SignInScreenProps } from "../auth.types";

export default function SignInScreen({
  onSwitchToSignUp,
  onSwitchToForgotPassword,
}: SignInScreenProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useAppDispatch();

  const isEmailValid = useMemo(() => emailRegex.test(email.trim()), [email]);
  const isPasswordValid = useMemo(() => password.length >= 6, [password]);

  // keep behavior identical: only blocks while signing-in (not by validation)
  const canSignIn = !isSigningIn;

  const onEmailChange = (text: string) => {
    setEmail(text);
    if (error) setError(null);
  };

  const onPasswordChange = (text: string) => {
    setPassword(text);
    if (error) setError(null);
  };

  const handleSignIn = async () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError("Please enter your email.");
      return;
    }
    if (!password) {
      setError("Please enter your password.");
      return;
    }
    if (!isEmailValid) {
      setError("Please enter a valid email.");
      return;
    }
    if (!isPasswordValid) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setError(null);
    setIsSigningIn(true);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        trimmedEmail,
        password,
      );
      const user = userCredential.user;

      let nameToSet = user.displayName || "";

      try {
        const savedName = await getUserName(user.uid);
        if (savedName) {
          nameToSet = savedName;
        }
      } catch (nameErr) {
        console.warn("Failed to load user name from Firestore:", nameErr);
      }

      if (nameToSet) {
        dispatch(setUserName(nameToSet));
      }
    } catch (err: any) {
      console.error("Sign in error:", err);

      if (err.code === "auth/invalid-credential") {
        setError("Invalid email or password. Please check your credentials.");
      } else if (err.code === "auth/user-not-found") {
        setError("No account found with this email. Please sign up first.");
      } else if (err.code === "auth/wrong-password") {
        setError("Invalid email or password. Please check your credentials.");
      } else if (err.code === "auth/invalid-email") {
        setError("Invalid email address format.");
      } else if (err.code === "auth/user-disabled") {
        setError("This account has been disabled. Please contact support.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many failed attempts. Please try again later.");
      } else if (err.code === "auth/network-request-failed") {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Failed to sign in. Please try again.");
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <SignInView
      email={email}
      password={password}
      error={error}
      isSigningIn={isSigningIn}
      canSignIn={canSignIn}
      onEmailChange={onEmailChange}
      onPasswordChange={onPasswordChange}
      onSubmitSignIn={handleSignIn}
      onSwitchToSignUp={onSwitchToSignUp}
      onSwitchToForgotPassword={onSwitchToForgotPassword}
    />
  );
}
